import * as Sentry from '@sentry/react';
import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { FeatureFlagProtectedRoute } from '@verantos-dev/app-state-base';
import {
  ANALYTICAL_VARIABLES_BASE_PATH,
  CONCEPT_GROUPS_BASE_PATH,
  DATA_RELIABILITY_BASE_PATH,
  ENTERPRISE_LOGIN_LOGOUT_PATH,
  ORGANIZATIONS_BASE_PATH,
  PROJECTS_BASE_PATH,
  // TODO: cleanup with research_assistant_developer_tools ff
  RA_SESSION_REVIEW_BASE_PATH,
  USERS_BASE_PATH,
} from '@verantos-dev/core-constants';
import { LogoutPage } from '@verantos-dev/el-feature-logout-page';
import { OrganizationsUiRoutes } from '@verantos-dev/organizations-ui-routes';
import {
  AnalyticalVariablesUiRoutes,
  ConceptGroupsUiRoutes,
  DataReliabilityUiRoutes,
  ProjectsUiRoutes,
} from '@verantos-dev/projects-ui-routes';
import { UsersUiRoutes } from '@verantos-dev/users-ui-routes';

import { VepAppPageLayoutContainer } from '../components/VepAppPageLayoutContainer';
import { VepLandingPage } from '../components/VepLandingPage';
import { useSentry } from '../hooks/useSentry';
import AgentTestClientPage from '../pages/AgentTestClientPage';
import { CodeListMapperPage } from '../pages/CodeListMapperPage';
// TODO: cleanup with research_assistant_developer_tools ff
import RaSessionReviewPage from '../pages/RaSessionReviewPage';
import ResearchAssistantPage from '../pages/ResearchAssistantPage';

const LazyAvIrCoverageReportPage = lazy(() => import('../pages/AvIrCoverageReportPage'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export function App() {
  useSentry();

  return (
    <SentryRoutes>
      <Route path={ENTERPRISE_LOGIN_LOGOUT_PATH} element={<LogoutPage />} />

      <Route element={<VepAppPageLayoutContainer />}>
        <Route path={`${PROJECTS_BASE_PATH}/*`} element={<ProjectsUiRoutes />} />
        <Route path={`${CONCEPT_GROUPS_BASE_PATH}/*`} element={<ConceptGroupsUiRoutes />} />
        {/* TODO: cleanup with data_reliability ff */}
        <Route
          path={`${DATA_RELIABILITY_BASE_PATH}/*`}
          element={
            <FeatureFlagProtectedRoute
              featureFlag="data_reliability"
              fallbackPath={PROJECTS_BASE_PATH}
              element={<DataReliabilityUiRoutes />}
            />
          }
        />
        <Route
          path={`${ANALYTICAL_VARIABLES_BASE_PATH}/*`}
          element={
            <FeatureFlagProtectedRoute
              featureFlag="research_assistant_gen_3"
              fallbackPath={PROJECTS_BASE_PATH}
              element={<AnalyticalVariablesUiRoutes />}
            />
          }
        />
        <Route path={`${USERS_BASE_PATH}/*`} element={<UsersUiRoutes />} />
        <Route path={`${ORGANIZATIONS_BASE_PATH}/*`} element={<OrganizationsUiRoutes />} />

        <Route
          path="/research-assistant-test"
          element={
            <FeatureFlagProtectedRoute
              featureFlag="agent-chat-interface"
              fallbackPath={PROJECTS_BASE_PATH}
              element={<AgentTestClientPage />}
            />
          }
        />
        <Route
          path="/av-ir-coverage"
          element={
            <FeatureFlagProtectedRoute
              featureFlag="av_ir_coverage_report"
              fallbackPath={PROJECTS_BASE_PATH}
              element={
                <Suspense fallback={null}>
                  <LazyAvIrCoverageReportPage />
                </Suspense>
              }
            />
          }
        />
        <Route path="/code-list-mapper" element={<CodeListMapperPage />} />
        <Route
          path="/research-assistant"
          element={
            <FeatureFlagProtectedRoute
              featureFlag="data_exploration"
              fallbackPath={PROJECTS_BASE_PATH}
              element={<ResearchAssistantPage />}
            />
          }
        />
        {/* TODO: cleanup with research_assistant_developer_tools ff — OR-6432 Block B.
            Internal-only, never nested under /projects/:id (sessions span
            projects/orgs by design). FeatureFlagProtectedRoute only checks the
            flag; RaSessionReviewPage itself checks isInternalUser before
            rendering the real content (see that file for why it doesn't use
            the withAuth/appIdToClaimCheckDefinitionMap role-claim pattern). */}
        <Route
          path={`/${RA_SESSION_REVIEW_BASE_PATH}`}
          element={
            <FeatureFlagProtectedRoute
              featureFlag="research_assistant_developer_tools"
              fallbackPath={PROJECTS_BASE_PATH}
              element={<RaSessionReviewPage />}
            />
          }
        />
      </Route>

      <Route path="*" element={<VepLandingPage />} />
    </SentryRoutes>
  );
}

export default App;
